/**
 * What CleanFile can actually do, per format.
 *
 * This table is the only place a support claim is written down, and both the interface and the
 * "not supported" error message read from it. The point is that a claim on the page cannot drift
 * away from what the code does, because there is nowhere else for a claim to live.
 *
 * - 'full'        CleanFile parses the container, removes metadata and re-reads the result to check.
 * - 'scan'        CleanFile reads and reports metadata but cannot rewrite the container safely.
 * - 'unsupported' CleanFile recognises the format and says so. It does not hand back a copy.
 *
 * Nothing is listed as 'full' unless a test cleans a real file of that format and re-parses the
 * output. There is deliberately no 'partial'.
 */

export type SupportLevel = 'full' | 'scan' | 'unsupported';

export interface FormatCapability {
  label: string;
  extensions: string;
  support: SupportLevel;
  /** What happens to a file of this format, in one sentence. */
  summary: string;
  /** True when cleaning never re-encodes the content — the claim behind "no quality loss". */
  contentUntouched: boolean;
  /** Why this format is not a full clean, when it is not. */
  limitation?: string;
}

export const FORMAT_CAPABILITIES: readonly FormatCapability[] = [
  {
    label: 'JPEG',
    extensions: '.jpg, .jpeg',
    support: 'full',
    summary:
      'Exif, XMP, IPTC, Photoshop blocks, comments and C2PA Content Credentials are cut out of the ' +
      'marker structure. The compressed scan data is copied across byte for byte.',
    contentUntouched: true,
  },
  {
    label: 'PNG',
    extensions: '.png',
    support: 'full',
    summary:
      'eXIf, tEXt, zTXt, iTXt, tIME and caBX (Content Credentials) chunks are removed and the ' +
      'checksums recomputed. The IDAT image data is copied across byte for byte.',
    contentUntouched: true,
  },
  {
    label: 'WebP',
    extensions: '.webp',
    support: 'full',
    summary:
      'EXIF, XMP and C2PA chunks are removed from the RIFF container and the VP8X feature flags ' +
      'corrected to match. The VP8 or VP8L image data is copied across byte for byte.',
    contentUntouched: true,
  },
  {
    label: 'HEIC / HEIF',
    extensions: '.heic, .heif',
    support: 'full',
    summary:
      'Exif, XMP and Content Credentials items are deleted from the data box and every item offset ' +
      'corrected, so the bytes are gone rather than orphaned. The picture items are untouched.',
    contentUntouched: true,
  },
  {
    label: 'AVIF',
    extensions: '.avif',
    support: 'full',
    summary:
      'Exif, XMP and Content Credentials items are deleted from the data box and every item offset ' +
      'corrected, so the bytes are gone rather than orphaned. The picture items are untouched.',
    contentUntouched: true,
  },
  {
    label: 'SVG',
    extensions: '.svg',
    support: 'full',
    summary:
      '<metadata> blocks, RDF/Dublin Core author information, editor namespaces such as Inkscape ' +
      'and Illustrator, and comments are removed. Every element that draws the picture is kept.',
    contentUntouched: true,
  },
  {
    label: 'Word document',
    extensions: '.docx',
    support: 'full',
    summary:
      'Both property parts, the revision history, and the metadata inside embedded pictures are ' +
      'cleaned. Every part CleanFile does not edit keeps its original compressed bytes.',
    contentUntouched: true,
  },
  {
    label: 'PDF',
    extensions: '.pdf',
    support: 'full',
    summary:
      'The document information dictionary, the XMP packet and the metadata inside embedded ' +
      'pictures are rewritten in place, to exactly the same length, so nothing else in the file moves.',
    contentUntouched: true,
    limitation:
      'Metadata packed inside a compressed object stream is reported but not removed, because ' +
      'rewriting one would change its length and invalidate every offset in the file.',
  },
  {
    label: 'GIF',
    extensions: '.gif',
    support: 'unsupported',
    summary: 'Recognised and named. CleanFile does not clean GIF files.',
    contentUntouched: false,
    limitation:
      'GIF carries metadata in comment and application extension blocks. Removing them is ' +
      'straightforward, but nothing here has been built or tested for it yet, so the format is ' +
      'refused rather than returned unchanged.',
  },
  {
    label: 'TIFF',
    extensions: '.tif, .tiff',
    support: 'unsupported',
    summary: 'Recognised and named. CleanFile does not clean TIFF files.',
    contentUntouched: false,
    limitation:
      'In a TIFF the metadata directory and the image data share one offset table, so removing a ' +
      'GPS tag means recalculating where every strip of the picture lives. Getting that wrong ' +
      'produces a file that opens to garbage, so it is refused until it can be done properly.',
  },
  {
    label: 'BMP',
    extensions: '.bmp',
    support: 'unsupported',
    summary: 'Recognised and named. CleanFile does not clean BMP files.',
    contentUntouched: false,
    limitation:
      'A BMP header has nowhere to put Exif, GPS or author information. There is normally nothing ' +
      'of this kind to remove, so the format is refused rather than given a misleading clean bill.',
  },
  {
    label: 'Icon file',
    extensions: '.ico',
    support: 'unsupported',
    summary: 'Recognised and named. CleanFile does not clean icon files.',
    contentUntouched: false,
    limitation:
      'An icon file is a directory of small bitmaps or PNGs with no metadata section of its own. ' +
      'Any metadata would sit inside an embedded PNG, which CleanFile does not reach into here.',
  },
];

/** The formats a user can hand over and get a cleaned copy back. */
export const FULLY_SUPPORTED = FORMAT_CAPABILITIES.filter((entry) => entry.support === 'full');

export function capabilityFor(label: string): FormatCapability | undefined {
  return FORMAT_CAPABILITIES.find((entry) => entry.label === label);
}

/** "JPG · PNG · WebP · …" — the short list under an upload area, built from the table itself. */
export function supportedFormatList(): string {
  return FULLY_SUPPORTED.map((entry) => entry.label).join(' · ');
}
